import React, { useContext, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import BottomDialog from "./BottomDialog.js";
import CenterDialog from "./CenterDialog.js";
import { useDimensions } from "src/utils/useDimensions.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import {
  getCalendarApiUrl,
  getWebcalFormat,
  handleOpenWebcal,
} from "src/utils/Utils.js";
import { useNavigate } from "react-router-dom";
import { getPageRoute } from "src/services/Config.js";
import { finalPulse } from "../ProgressButton.js";

const OpenCalendarDialogContent = ({ handleOpenCalendar }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        padding: 2,
      }}
    >
      <Typography variant="title" sx={{ fontSize: "26px", textWrap: "pretty" }}>
        Next Stop: <br />
        Your Calendar App
      </Typography>
      <Typography variant="body1" sx={{ mt: 4 }}>
        Follow the instructions in your calendar app to finish subscribing.
      </Typography>
      <Typography variant="body1" sx={{ mt: 3 }}>
        Once you're done, pop back here to confirm so we can make sure your subscription stays active – and if it
        doesn't go through, we'll help you fix it.
      </Typography>
      <Button
        variant="contained"
        onClick={handleOpenCalendar}
        sx={{
          mt: 4,
          width: "100%",
          maxWidth: "300px",
          display: "flex",
          alignSelf: "center",
          animation: `${finalPulse} 3s infinite`,
        }}
      >
        OPEN CALENDAR
      </Button>
    </Box>
  );
};

const OpenCalendarDialog = ({ open, handleClose, calendar }) => {
  const { isSmallScreen } = useDimensions();
  const { user } = useContext(AuthContext);
  const calendarApiUrl = getCalendarApiUrl({ calendar, user });
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      customEvent({
        name: "landed_on_open_calendar_dialog",
        category: "SubscribingCalendar",
        label: "Landed on Open Calendar Dialog",
        data: {
          user_id: user?._id,
          user_email: user?.email,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenCalendar = () => {
    customEvent({
      name: `user_click_open_calendar`,
      category: "SubscribingCalendar",
      label: "Open Calendar Click",
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
      },
    });

    customEvent({
      name: `click_open_calendar_${user.provider}`,
      category: "SubscribingCalendar",
      label: "Open Calendar Click",
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
      },
    });

    handleOpenWebcal(user, getWebcalFormat(calendarApiUrl), calendar);
    navigate(
      getPageRoute(
        "help",
        "SUBSCRIBING_CALENDAR_VERIFY",
        {
          handle: calendar.handle,
          calendarId: calendar._id,
        },
        { email: user.email, is_reminder: false }
      ),
      {
        state: { calendar },
      }
    );
  };

  return (
    <>
      {isSmallScreen ? (
        <BottomDialog
          open={open}
          handleClose={handleClose}
          hiddenCloseButton
          hiddenRectangle
          bubbleStyle
        >
          <OpenCalendarDialogContent handleOpenCalendar={handleOpenCalendar} />
        </BottomDialog>
      ) : (
        <CenterDialog
          open={open}
          handleClose={handleClose}
          sx={{ height: "auto", ".MuiPaper-root": { maxWidth: "393px" } }}
          hideCloseButton
        >
          <OpenCalendarDialogContent handleOpenCalendar={handleOpenCalendar} />
        </CenterDialog>
      )}
    </>
  );
};

export default OpenCalendarDialog;
